import {
  courseUserSelectors,
  createAppSelector,
  sectionSelectors,
  selectCurrentSectionId,
} from "./base";

import type { CourseUserType, SectionType } from "components/server-types";

// Course-admin status is now derived purely from the presence of a CourseUser row for the
// section's course; OrgUser.role no longer carries a "course_admin" value.
const getCourseRole = (courseUsers: CourseUserType[], section: SectionType | null) =>
  courseUsers.find((cu) => cu.course_id === section?.course_id)?.role || null;

const getCourseRoleForCurrentSection = (
  courseUsers: CourseUserType[],
  sections: Record<string, SectionType | null>,
  currentSectionId: string | null,
) => {
  const section = currentSectionId ? sections[currentSectionId] : null;
  return getCourseRole(courseUsers, section);
};

// NOTE: Because this is used by the permissions selector, we can't use selectCurrentSection
// and have to handle it manually here to avoid circular dependencies.  Perhaps move
// selectCurrentSection to base.ts if we need to use it in other selectors?
export const selectCurrentCourseRole = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, (state) => state.sections, selectCurrentSectionId],
  (courseUsers, sections, currentSectionId) =>
    getCourseRoleForCurrentSection(courseUsers, sections, currentSectionId),
);

export const selectCourseRoleByUserId = createAppSelector(
  [courseUserSelectors.filterByUserId, (state) => state.sections, selectCurrentSectionId],
  (courseUsers, sections, currentSectionId) =>
    getCourseRoleForCurrentSection(courseUsers, sections, currentSectionId),
);

export const selectCourseUserByCourseId = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, (_state, courseId: string | null) => courseId],
  (courseUsers, courseId) => courseUsers.find((cu) => cu.course_id === courseId) || null,
);

export const selectCourseRoleBySectionId = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, sectionSelectors.selectById],
  (courseUsers, section) => getCourseRole(courseUsers, section),
);

export const selectCourseUsersForCurrentSection = createAppSelector(
  [courseUserSelectors.selectAll, (state) => state.sections, selectCurrentSectionId],
  (courseUsers, sections, currentSectionId) => {
    const currentSection = currentSectionId ? sections[currentSectionId] : null;
    return courseUsers.filter((cu) => cu.course_id === currentSection?.course_id) || [];
  },
);

// True if the user has any CourseUser rows. Course-admin status is now derived from
// CourseUser presence rather than an OrgUser.role of "course_admin".
export const selectIsUserACourseAdmin = createAppSelector(
  [courseUserSelectors.filterByUserId],
  (courseUsers) => courseUsers.length > 0,
);
